import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { OfferViewModel, AppliedOfferListItem } from '../../../../core/models/offer-view.model';
import { FeatureFlagsService } from '../../../../core/services/feature-flags.service';
import { OfferMediaMoleculeComponent } from '../../molecules/offer-media/offer-media.component';
import { OfferInfoMoleculeComponent } from '../../molecules/offer-info-molecule/offer-info-molecule.component';
import { OfferRatingMoleculeComponent, OfferRatingSubmit } from '../../molecules/offer-rating-molecule/offer-rating-molecule.component';
import { UiSponsorBadgeAtomComponent } from '../../atoms/ui-sponsor-badge/ui-sponsor-badge.component';
import { UiBenefitBadgeAtomComponent } from '../../atoms/ui-benefit-badge/ui-benefit-badge.component';

export type CardOfferType = 'normal' | 'nueva' | 'aplication';
type LabelColor = 'blue' | 'orange' | 'green';
interface LabelConfig { color: LabelColor; img: string; showLabel: boolean; }

const LABEL_CONFIG: Record<string, LabelConfig> = {
  '0': { color: 'blue', img: '', showLabel: false },
  '1': { color: 'orange', img: 'assets/icons/oferta_destacada_estrella.svg', showLabel: true },
  '2': { color: 'green', img: 'assets/icons/icono_etiqueta_nueva.svg', showLabel: true },
  '3': { color: 'blue', img: 'assets/icons/icono_etiqueta_regular.svg', showLabel: true },
};

const STATUS_CLASS: Record<string, string> = {
  enproceso: 'state-p', preaprobado: 'state-a', aprobado: 'state-a',
  rechazado: 'state-r', expirado: 'state-r', tiempovencido: 'state-r',
};

@Component({
  selector: 'app-card-offer',
  imports: [
    DatePipe,
    NgClass,
    OfferMediaMoleculeComponent,
    OfferInfoMoleculeComponent,
    OfferRatingMoleculeComponent,
    // UiSponsorBadgeAtomComponent,
    UiBenefitBadgeAtomComponent,
  ],
  templateUrl: './card-offer.component.html',
  styleUrls: [
    '../../../card/card.component.scss',
    './card-offer.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardOfferOrganismComponent {
  private readonly featureFlags = inject(FeatureFlagsService);

  readonly data = input.required<OfferViewModel | AppliedOfferListItem>();
  readonly type = input<CardOfferType>('normal');
  readonly index = input(0);
  readonly continueLoading = input(false);
  // 'none'|'ok'|'error' — el contenedor smart lo setea tras la llamada al servicio de rating
  readonly ratingResult = input<'none' | 'ok' | 'error'>('none');

  readonly applyPressed = output<void>();
  readonly retryPressed = output<void>(); // "Retomar aplicación" → padre llama redirect()
  readonly continuePressed = output<void>(); // preselecta "Continuar" → padre llama continueApply()
  readonly ratingSubmitted = output<OfferRatingSubmit>();
  readonly clearPreselecta = output<void>();
  readonly alertClosed = output<void>();
  // "Ver más detalles": el contenedor smart abre app-offer-detail-view en una pestaña nueva
  readonly detailPressed = output<void>();

  readonly showRating = signal(false);

  protected readonly offer = computed(() => this.data() as OfferViewModel);
  protected readonly applied = computed(() => this.data() as AppliedOfferListItem);

  protected readonly cardForTC = computed(() =>
    (this.offer().detail?.subcategories ?? []).includes('TC')
  );
  protected readonly hasPreselecta = computed(() =>
    this.offer().preselecta.orderNumber != undefined
  );
  protected readonly labelConfig = computed((): LabelConfig =>
    LABEL_CONFIG[this.offer().labelType ?? '0'] ?? LABEL_CONFIG['0']
  );
  protected readonly logoUrl = computed(() => {
    const o = this.offer();
    return o.detail.companyLogoFlag ? (o.detail.offerLogoURL ?? '') : (o.company.entityimg ?? '');
  });

  protected readonly hasAdditionalBenefits = computed(() =>
    Boolean(this.offer().detail?.additionalBenefits && this.offer().detail?.textAdditionalBenefits?.trim())
  );

  protected readonly tabTitle = computed(() => {
    const offer = this.offer();

    return `${offer.company.name} - ${offer.card.productName}`.trim();
  });

  protected readonly tabTitleIsLong = computed(() =>
    this.tabTitle().length > 28
  );

  protected readonly score = computed(() => parseInt(this.offer().detail.score ?? '0'));
  protected readonly buttonState = computed((): 1 | 2 | 3 => {
    const o = this.offer();
    if (!o.isApplied) return 1;
    if (o.detail?.urlRedirect != undefined || o.urlRedirect !== '') return 2;
    return 3;
  });
  protected readonly labelId = computed(() => (this.offer().company.id ?? '') + this.offer().offerId);
  protected readonly switchId = computed(() => 'rating-switch-' + this.index());
  protected readonly stateClass = computed(() => {
    const s = this.applied().status?.replace(' ', '').toLowerCase() ?? '';
    return STATUS_CLASS[s] ?? '';
  });
  protected readonly showCard = computed(() => {
    if (this.type() === 'aplication') return true;
    const id = this.offer().company?.id ?? this.offer().preselecta?.company?.id;
    if (!id) return true;
    const flag = ({ '901780224': 'showCredit365', '890903938': 'showBancolombia' } as Record<string, string>)[id];
    return flag ? this.featureFlags.isEnabled(flag) : true;
  });
}
